import type { Edge, Node } from "@xyflow/react";
import type { CardGeometry, Point } from "../model/cardGeometry";
import type { NodeRun } from "../model/graphRun";
import { absoluteOrigins, type EdgeRoute } from "../model/edgeRoutes";
import type { NodeBox } from "../model/graphLayout";
import type { PlacementAxes } from "../model/placementAxes";
import { isExpandable, type GraphEdge, type GraphNode, type WorkflowGraph } from "../model/workflowGraph";
import type { CardInteraction } from "./cardInteraction";
import type { DetailContext } from "./detailLines";
import type { NodeText } from "./nodeText";

/**
 * The graph model and its layout as React Flow nodes and edges, for the read view and the
 * editor alike. Nothing here decides what is shown or where; it only translates (P-14:
 * React Flow only renders). Each port is a handle, so React Flow knows which edge belongs
 * where; the edge's path comes from the layout's anchors (`edgeRoutes`), never from where
 * React Flow measured the handle.
 */

/** What the view knows about each node beyond the graph model. */
export interface NodeDescription {
  /** What it shows of the Run on the run graph; `null` on the Workflow graph, or before C names it. */
  readonly run: NodeRun | null;
  /** A step card's placement axes as module B's plan says them (WD-1+3); `null` without a plan, as in the editor. */
  readonly axes: PlacementAxes | null;
  readonly text: NodeText;
  /** A card's geometry; `null` for an open frame, which holds other nodes. */
  readonly geometry: CardGeometry | null;
  /** An open frame's port anchors; `null` for a card. */
  readonly frameAnchors: ReadonlyMap<string, Point> | null;
  /** Its card is open: it shows its settings. */
  readonly open: boolean;
  /** What an open card's settings need beyond the node; `null` while closed. */
  readonly details: Omit<DetailContext, "promptSections"> | null;
  /** What the card offers: its name's action, its chevron, its marks, its state link, its ports. */
  readonly interaction: CardInteraction;
}

export interface ProcessNodeData extends NodeDescription, Record<string, unknown> {
  readonly node: GraphNode;
}

export type ProcessFlowNode = Node<ProcessNodeData, "processCard" | "processFrame">;

export interface PortEdgeData extends Record<string, unknown> {
  readonly route: EdgeRoute;
  readonly kind: GraphEdge["kind"];
  /** A `then` into a step that waits for Run: dotted (`edgeClassName`). */
  readonly waitsForRun: boolean;
  /** Where the layout put both cards: a card dragged away from it takes the edge's end along. */
  readonly sourceOrigin: Point;
  readonly targetOrigin: Point;
}

export type PortFlowEdge = Edge<PortEdgeData, "port">;

/** The handles a `then` arrow runs between: the cards' sides at header height. */
export const THEN_HANDLES = { in: "then:in", out: "then:out" } as const;


export function flowElements(
  graph: WorkflowGraph,
  boxes: ReadonlyMap<string, NodeBox>,
  routes: ReadonlyMap<string, EdgeRoute>,
  describe: (node: GraphNode) => NodeDescription,
): { nodes: ProcessFlowNode[]; edges: PortFlowEdge[] } {
  const depthOf = new Map(graph.nodes.map((node) => [node.id, node.depth]));
  const origins = absoluteOrigins(graph, boxes);
  const onRequest = new Set(graph.nodes.filter((node) => node.kind === "step" && node.start === "on_request").map((node) => node.id));
  const nodes = graph.nodes.flatMap((node): ProcessFlowNode[] => {
    const box = boxes.get(node.id);
    if (box === undefined) {
      return [];
    }
    const described = describe(node);
    return [
      {
        id: node.id,
        type: isExpandable(node) && node.expanded ? "processFrame" : "processCard",
        position: { x: box.x, y: box.y },
        width: box.width,
        height: box.height,
        style: { width: box.width, height: box.height },
        ...(node.parentId === null ? {} : { parentId: node.parentId, extent: "parent" as const }),
        data: { node, ...described },
        ariaLabel: described.text.label,
        // In the editor the order is the draft's; where a step is dropped along the row says where it goes.
        draggable: described.interaction.draggable,
        connectable: described.interaction.connectable,
        selectable: false,
        // The node's buttons and links take the focus; the node itself is a labelled group.
        focusable: false,
        // An open card stands above its neighbours.
        ...(described.open ? { zIndex: 10 + node.depth } : {}),
      },
    ];
  });
  const edges = graph.edges.flatMap((edge): PortFlowEdge[] => {
    const route = routes.get(edge.id);
    const sourceOrigin = origins.get(edge.source);
    const targetOrigin = origins.get(edge.target);
    if (route === undefined || sourceOrigin === undefined || targetOrigin === undefined) {
      return [];
    }
    return [
      {
        id: edge.id,
        type: "port",
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourcePort ?? THEN_HANDLES.out,
        targetHandle: edge.targetPort ?? THEN_HANDLES.in,
        data: { route, kind: edge.kind, waitsForRun: onRequest.has(edge.target), sourceOrigin, targetOrigin },
        // Edges between steps inside an open frame are drawn above the frame.
        zIndex: Math.max(depthOf.get(edge.source) ?? 0, depthOf.get(edge.target) ?? 0),
        focusable: false,
        selectable: false,
      },
    ];
  });
  return { nodes, edges };
}
